import { ArrowRight, Copy, LockKeyhole } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '../auth'
import { computeAfterSalesAdjusted, computePinduoduoUnitProfit, type AfterSalesInputs, type CalculatorInputs } from '../pinduoduoProfitCalculator'
import { money } from '../utils'

interface SharedRecord {
  id: string
  code?: string
  savedAt: string
  productName: string
  variantAttributes: string
  variantId: string
  note?: string
  inputs: CalculatorInputs
  afterSales?: AfterSalesInputs
  createdByName: string
  accessLevel: 'owner' | 'read' | 'edit'
  canEdit: boolean
  canCopy: boolean
  revision: number
  lastEditedByName: string
  updatedAt: number
}

const percent = (value: number | null) => value === null ? '—' : `${(value * 100).toFixed(2)}%`

export function SharedCalculatorRecordPage() {
  const { platform = '', recordId = '' } = useParams()
  const auth = useAuth()
  const [record, setRecord] = useState<SharedRecord | null>(null)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)
  const normalizedPlatform = platform === 'pinduoduo' ? 'pinduoduo' : 'jd'
  const calculatorRoute = normalizedPlatform === 'jd' ? '/jd/profit-calculator' : '/pinduoduo/profit-calculator'

  useEffect(() => {
    let active = true
    fetch(`/api/shared-calculations/${normalizedPlatform}/${encodeURIComponent(recordId)}`, { cache: 'no-store' })
      .then(async (response) => {
        const payload = await response.json() as { record?: SharedRecord; error?: string }
        if (!response.ok || !payload.record) throw new Error(payload.error || '分享记录加载失败')
        return payload.record
      })
      .then((next) => { if (active) setRecord(next) })
      .catch((reason) => { if (active) setError(reason instanceof Error ? reason.message : String(reason)) })
    return () => { active = false }
  }, [normalizedPlatform, recordId])

  if (error) return <div className="shared-calculator-state"><LockKeyhole size={28} /><h1>无法查看这条测算</h1><p>{error}</p></div>
  if (!record) return <div className="loading-state">正在加载分享的测算结果…</div>

  const result = computePinduoduoUnitProfit(record.inputs)
  const afterSales = record.afterSales
    ? computeAfterSalesAdjusted(result, record.inputs, record.afterSales)
    : null
  const updatedAt = new Date(record.updatedAt * 1000).toLocaleString('zh-CN', { hour12: false })
  const canOpenCalculator = auth.canAccess(
    normalizedPlatform === 'jd' ? 'jd-588485' : 'pdd-beijuxiong-youhe',
    normalizedPlatform === 'jd' ? 'jd.profit_calculator' : 'pdd.profit_calculator',
  )
  const openLabel = record.canEdit ? '进入编辑' : '打开并另存为我的测算'

  return (
    <div className="shared-calculator-page">
      <div className="shared-calculator-heading">
        <div>
          <span className="shared-calculator-kicker">分享给我的 / {normalizedPlatform === 'jd' ? '京东' : '拼多多'}利润测算</span>
          <h1>{record.note || record.productName}</h1>
          <p>{record.code || record.id} · 所有者 {record.createdByName}</p>
        </div>
        <div className="shared-calculator-actions">
          <span className={`calculator-access-badge is-${record.accessLevel}`}>{record.accessLevel === 'owner' ? '所有者' : record.accessLevel === 'edit' ? '可编辑' : '只读'}</span>
          {canOpenCalculator && record.canCopy && <Link className="primary-button" to={`${calculatorRoute}?record=${encodeURIComponent(record.id)}`}>{openLabel}<ArrowRight size={15} /></Link>}
        </div>
      </div>
      <div className="shared-calculator-live">
        <div><b>实时共享结果</b><span>所有者或可编辑同事保存后，本页同步展示最新版本。</span></div>
        <div><b>版本 {record.revision}</b><span>最后由 {record.lastEditedByName} 编辑 · {updatedAt}</span></div>
      </div>
      <div className="shared-calculator-grid">
        <section className="overview-section shared-calculator-result">
          <div className="section-heading-row"><div><h2>测算结果</h2><p>{record.productName} · {record.variantAttributes || '默认规格'} · SKU {record.variantId}</p></div></div>
          <div className="shared-calculator-metrics">
            <div><span>含返点利润</span><strong className={result.profitWithRebate < 0 ? 'is-negative' : ''}>{money(result.profitWithRebate)}</strong></div>
            <div><span>利润率</span><b>{percent(result.profitWithRebateRate)}</b></div>
            <div><span>保本ROI</span><b>{result.breakEvenRoi === null ? '—' : result.breakEvenRoi.toFixed(2)}</b></div>
            {afterSales && <div><span>考虑售后利润</span><b>{money(afterSales.expectedProfitPerOrdered)}</b></div>}
          </div>
          <div className="shared-calculator-formula">
            {money(record.inputs.price)} − {money(result.fulfillmentCost)} − {money(result.rateFee)} ＋ {money(result.rebateAmount)} ＝ <b>{money(result.profitWithRebate)}</b>
          </div>
          <div className="shared-calculator-costs">
            <div><span>售价</span><b>{money(record.inputs.price)}</b></div>
            <div><span>产品成本</span><b>{money(record.inputs.productCost)}</b></div>
            <div><span>包装＋赠品＋运费险＋运费</span><b>{money(result.fulfillmentCost - record.inputs.productCost)}</b></div>
            <div><span>平台扣点＋税点</span><b>{money(result.rateFee)}</b></div>
          </div>
        </section>
        <aside className="shared-calculator-permission">
          <h2>访问说明</h2>
          <dl><dt>当前账号</dt><dd>{auth.user?.username}</dd><dt>权限</dt><dd>{record.accessLevel === 'edit' ? '可编辑同一记录' : record.accessLevel === 'owner' ? '所有者' : '只读'}</dd><dt>所有者</dt><dd>{record.createdByName}</dd><dt>最后编辑</dt><dd>{record.lastEditedByName}</dd></dl>
          <p><LockKeyhole size={14} />链接仍需登录并通过服务端记录权限校验，转发链接不会自动授权。</p>
          <button type="button" className="jdc-btn-ghost" onClick={() => { void navigator.clipboard?.writeText(window.location.href); setCopied(true); window.setTimeout(() => setCopied(false), 1500) }}><Copy size={14} />{copied ? '已复制' : '复制当前链接'}</button>
        </aside>
      </div>
    </div>
  )
}
